import React from 'react';
import { AppProps, AppContext } from 'next/app';
import NextApp from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import type { NextPage } from 'next';
import { QueryClient, QueryClientProvider } from 'react-query';

import 'styles/globals.css';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function App({ Component, pageProps }: AppPropsWithLayout) {
  const [queryClient] = React.useState(() => new QueryClient());
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <QueryClientProvider client={queryClient}>
      {getLayout(<Component {...pageProps} />)}
    </QueryClientProvider>
  );
}

// Disables Automatic Static Optimization for every page in the app, so
// every page is server-side rendered per request instead of prerendered
// once at `next build` time. Required here because several
// pages/components read next.config.js's publicRuntimeConfig (via
// getConfig()) to build outbound links (e.g. components/navigation/
// navigation.tsx, components/shared/Header/Header.tsx) — without this,
// those values get frozen at build time (confirmed empirically), which
// breaks the "one image, env vars set directly on the Cloud Run service"
// deploy model this repo needs. Matches keyzy-fe's pages/_app.tsx, which
// has this same override enabled for the same reason.
App.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await NextApp.getInitialProps(appContext);

  return { ...appProps };
};

export default App;
